import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  CircularProgress,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Switch,
  FormControlLabel,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { StructuredWorkoutIcon } from '../../icons'; // Re-using the same icon for structured items
import { getMedicationSuggestions, MedicationSuggestion, medicationService } from '../../../services/medication.service'; // Import medicationService
import { CalendarEventDto } from '../../../types/calendar';
import { ModalIndices } from '../../../common/i-doc/i-doc.registry.ts';
import { localNowIso, toLocalIso } from '@/utils/datetime';

interface AddMedicationEventModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: {
    medicationId?: number;
    customName?: string;
    qty?: number;
    dosage?: string; // Added dosage
    scheduledLdt: string; // local datetime string (no Z suffix)
    isCompleted: boolean;
    calendarId?: number;
  }) => void;
  calendarId?: number;
  eventToEdit?: CalendarEventDto | null;
  initialScheduledAt?: Date;
}

type SelectedMedication = {
  id: number | null;
  name: string;
  form: string;
  dosage: string[]; // Changed from availableDosages: string[]
  isStructured: boolean;
};

export const AddMedicationEventModal: React.FC<AddMedicationEventModalProps> = ({ open, onClose, onSave, calendarId, eventToEdit, initialScheduledAt }) => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<MedicationSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMedicationDetails, setLoadingMedicationDetails] = useState(false); // New loading state
  const [selectedMedication, setSelectedMedication] = useState<SelectedMedication | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);

  const [qty, setQty] = useState('1'); // Default to 1
  const [selectedDosage, setSelectedDosage] = useState(''); // New state for selected dosage
  const [scheduledAt, setScheduledAt] = useState(localNowIso().slice(0, 16));
  const [showDateTimePicker, setShowDateTimePicker] = useState(false);

  const getOptionLabel = (option: MedicationSuggestion | SelectedMedication): string => {
    if (!option) return '';
    if (option.id === null) return option.name;
    if (option.form && option.form !== 'інше') {
      return `${option.name} (${option.form})`;
    }
    return option.name;
  };

  useEffect(() => {
    if (open) {
      const now = new Date();
      if (eventToEdit) {
        const eventDate = new Date(eventToEdit.scheduledLdt || now);
        setScheduledAt(toLocalIso(eventDate).slice(0, 16));
        setShowDateTimePicker(eventDate > now);

        if (eventToEdit.associationType === 'Medication' && eventToEdit.associationId) {
          setLoadingMedicationDetails(true);
          medicationService.getMedicationById(eventToEdit.associationId)
            .then(fullMedicationDetails => {
              const med: SelectedMedication = {
                id: fullMedicationDetails.id,
                name: fullMedicationDetails.name,
                form: fullMedicationDetails.form,
                dosage: fullMedicationDetails.dosage || [],
                isStructured: fullMedicationDetails.isStructured,
              };
              setSelectedMedication(med);
              setInputValue(getOptionLabel(med));
              setQty(eventToEdit.details?.qty?.toString() || '1');
              setSelectedDosage(eventToEdit.details?.dosage || (med.dosage.length > 0 ? med.dosage[0] : ''));
              setFetchError(null);
            })
            .catch(error => {
              setFetchError(error.response?.data?.message || 'Не вдалося завантажити деталі препарату.');
              setSelectedMedication(null);
              setInputValue(eventToEdit.title || '');
              setQty('');
            })
            .finally(() => {
              setLoadingMedicationDetails(false);
            });
        } else {
          // Not a structured event, just a custom name
          setSelectedMedication(null);
          setInputValue(eventToEdit.title || '');
          setQty('');
          setSelectedDosage('');
        }
      } else {
        // Reset for new event
        setInputValue('');
        setOptions([]);
        setSelectedMedication(null);
        setQty('1');
        setSelectedDosage('');
        setFetchError(null);
        const initDate = initialScheduledAt ?? now;
        setScheduledAt(toLocalIso(initDate).slice(0, 16));
        setShowDateTimePicker(Boolean(initialScheduledAt));
      }
    } else {
      // Cleanup on close
      setInputValue('');
      setOptions([]);
      setSelectedMedication(null);
      setQty('1');
      setSelectedDosage('');
      setFetchError(null);
      setScheduledAt(localNowIso().slice(0, 16));
      setShowDateTimePicker(false);
      setLoading(false);
      setLoadingMedicationDetails(false);
    }
  }, [open, eventToEdit]);

  useEffect(() => {
    if (selectedMedication && getOptionLabel(selectedMedication) !== inputValue) {
      setSelectedMedication(null);
      setSelectedDosage(''); // Clear dosage if medication changes
    }
  }, [inputValue, selectedMedication]);

  useEffect(() => {
    if (selectedMedication && selectedMedication.dosage && selectedMedication.dosage.length > 0 && !selectedDosage) {
      setSelectedDosage(selectedMedication.dosage[0]); // Set first dosage as default if available
    }
  }, [selectedMedication, selectedDosage]);

  const fetchMedicationSuggestions = useCallback(async (query: string) => {
    if (query.length < 2) {
      setOptions([]);
      setFetchError(null);
      return;
    }
    setLoading(true);
    setFetchError(null);
    try {
      const suggestions = await getMedicationSuggestions(query);
      setOptions(suggestions);
    } catch (error: any) {
      console.error('Failed to fetch medication suggestions:', error);
      setFetchError(error.response?.data?.message || 'Не вдалося завантажити пропозиції препаратів.');
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (inputValue) {
        fetchMedicationSuggestions(inputValue);
      } else {
        setOptions([]);
        setFetchError(null);
      }
    }, 300);

    return () => clearTimeout(handler);
  }, [inputValue, fetchMedicationSuggestions]);

  const handleSave = () => {
    const trimmedInput = inputValue.trim();
    if (!trimmedInput) return;

    const finalScheduledAt = showDateTimePicker ? new Date(scheduledAt) : new Date();
    const isCompleted = !showDateTimePicker || finalScheduledAt <= new Date();

    const payload: AddMedicationEventModalProps['onSave'] extends (data: infer T) => any ? T : never = {
      scheduledLdt: toLocalIso(finalScheduledAt),
      isCompleted: isCompleted,
      calendarId: calendarId,
    };

    if (selectedMedication?.id) {
      payload.medicationId = selectedMedication.id;
      if (selectedMedication.isStructured) {
        if (qty && parseFloat(qty) > 0) {
          payload.qty = parseFloat(qty);
        }
        if (selectedDosage) {
          payload.dosage = selectedDosage;
        }
      }
    } else {
      payload.customName = trimmedInput;
    }

    onSave(payload);
  };

  const handleAutocompleteChange = (_event: any, newValue: MedicationSuggestion | string | null) => {
    if (typeof newValue === 'string') {
      setSelectedMedication({ id: null, name: newValue, form: '', dosage: [], isStructured: false });
      setSelectedDosage('');
    } else {
      setSelectedMedication(newValue as SelectedMedication | null);
      if (newValue && newValue.dosage && newValue.dosage.length > 0) {
        setSelectedDosage(newValue.dosage[0]); // Set first dosage as default
      } else {
        setSelectedDosage('');
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth data-i-doc={ModalIndices.ADD_MEDICATION_EVENT}>
      <DialogTitle>{eventToEdit ? 'Редагувати прийом' : 'Додати прийом препаратів'}</DialogTitle>
      <DialogContent>
        {loadingMedicationDetails ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Autocomplete
              id="medication-autocomplete"
              freeSolo
              value={inputValue}
              fullWidth
              sx={{ mt: 1 }}
              open={autocompleteOpen}
              onOpen={() => {
                if (inputValue.length > 1) {
                  setAutocompleteOpen(true);
                }
              }}
              onClose={() => setAutocompleteOpen(false)}
              options={options}
              loading={loading}
              loadingText="Завантаження..."
              noOptionsText="Препарат не знайдено. Натисніть Enter, щоб додати."
              getOptionLabel={(option) => typeof option === 'string' ? option : getOptionLabel(option as MedicationSuggestion)}
              filterOptions={(x) => x}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onInputChange={(_event, newInputValue) => {
                setInputValue(newInputValue);
                if (!newInputValue || newInputValue.length < 2) {
                  setAutocompleteOpen(false);
                } else if (newInputValue !== (selectedMedication ? getOptionLabel(selectedMedication) : '')) {
                  setSelectedMedication(null);
                }
              }}
              onChange={handleAutocompleteChange}
              disableClearable={true}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1">{option.name}</Typography>
                    {option.form && option.form !== 'інше' && (
                      <Typography variant="body2" color="textSecondary">({option.form})</Typography>
                    )}
                    {option.isStructured && (
                      <StructuredWorkoutIcon fontSize="small" color="action" />
                    )}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  autoFocus
                  label="Назва препарату"
                  variant="standard"
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {loading ? <CircularProgress color="inherit" size={20} /> : null}
                        {!loading && !inputValue && <SearchIcon color="action" />}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
            />

            {fetchError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {fetchError}
              </Alert>
            )}

            {selectedMedication?.isStructured && (
              <>
                {selectedMedication?.dosage && selectedMedication.dosage.length > 0 && (
                  <FormControl variant="standard" fullWidth sx={{ mt: 2 }}>
                    <InputLabel>Дозування</InputLabel>
                    <Select
                      value={selectedDosage}
                      onChange={(e) => setSelectedDosage(e.target.value)}
                      label="Дозування"
                    >
                      {selectedMedication.dosage.map((dosageOption) => (
                        <MenuItem key={dosageOption} value={dosageOption}>
                          {dosageOption}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <Box display="flex" gap={2} mt={3} alignItems="flex-end">
                  <TextField
                    label="Кількість"
                    type="number"
                    value={qty}
                    onChange={(e) => setQty(e.target.value)}
                    variant="standard"
                    sx={{ flexGrow: 1 }}
                    inputProps={{ min: "0.1", step: "0.1" }}
                  />
                  {selectedMedication?.form && (
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      {selectedMedication.form}
                    </Typography>
                  )}
                </Box>
              </>
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={showDateTimePicker}
                  onChange={(e) => setShowDateTimePicker(e.target.checked)}
                  name="scheduleEvent"
                  color="primary"
                />
              }
              label="Запланувати на інший час"
              sx={{ mt: 2 }}
            />

            {showDateTimePicker && (
              <TextField
                label="Час прийому"
                type="datetime-local"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                fullWidth
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Скасувати</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!inputValue.trim() || (selectedMedication?.id && !qty && parseFloat(qty) > 0) || (!selectedMedication?.id && !inputValue.trim())}
        >
          Зберегти
        </Button>
      </DialogActions>
    </Dialog>
  );
};
